/**
 * Canonical cross-portal URLs and helper utilities.
 * Enables seamless navigation and cross-linking between Marketing, Institution Portal,
 * Parent/Tenant Portal, and Admin Console across both local development and production.
 */

export interface PortalUrls {
  marketing: string
  /** School operator portal (bursars, principals, teachers). */
  school: string
  /** Property operator portal (managing agents, landlords). */
  property: string
  /** @deprecated There is no single institution portal; use `school` or `property`. */
  institution: string
  parent: string
  tenant: string
  admin: string
}

// Local ports are one per app, fixed in each app's vite.config.ts. They used
// to collide (marketing and parent both on 3002), so a "Parent" link could
// open the tenant portal.
const DEFAULTS = {
  marketing: { env: 'VITE_MARKETING_URL', local: 'http://localhost:3002', prod: 'https://lipaflow.co.ke' },
  school: { env: 'VITE_SCHOOL_PORTAL_URL', local: 'http://localhost:3000', prod: 'https://school.lipaflow.co.ke' },
  property: { env: 'VITE_PROPERTY_PORTAL_URL', local: 'http://localhost:3004', prod: 'https://property.lipaflow.co.ke' },
  parent: { env: 'VITE_PARENT_PORTAL_URL', local: 'http://localhost:3003', prod: 'https://parent.lipaflow.co.ke' },
  tenant: { env: 'VITE_TENANT_PORTAL_URL', local: 'http://localhost:3005', prod: 'https://tenant.lipaflow.co.ke' },
  admin: { env: 'VITE_ADMIN_PORTAL_URL', local: 'http://localhost:3001', prod: 'https://admin.lipaflow.co.ke' },
} as const

export function getPortalUrls(): PortalUrls {
  const isBrowser = typeof window !== 'undefined'
  const isLocalhost = isBrowser && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.endsWith('.local')
  )

  // Allow environment variable overrides when provided (e.g. staging or custom ports)
  const metaEnv = (import.meta.env ?? {}) as Record<string, string | undefined>
  const resolve = (key: keyof typeof DEFAULTS) => {
    const d = DEFAULTS[key]
    return (metaEnv[d.env] || (isLocalhost ? d.local : d.prod)).replace(/\/$/, '')
  }

  const school = resolve('school')
  return {
    marketing: resolve('marketing'),
    school,
    property: resolve('property'),
    // VITE_INSTITUTION_PORTAL_URL predates the school/property split.
    institution: metaEnv.VITE_INSTITUTION_PORTAL_URL || school,
    parent: resolve('parent'),
    tenant: resolve('tenant'),
    admin: resolve('admin'),
  }
}

/** The payer portal that pairs with an operator vertical. */
export function getPayerPortalUrl(verticalCode: string | undefined): string {
  const urls = getPortalUrls()
  return verticalCode === 'rental' ? urls.tenant : urls.parent
}
